import React, { Suspense } from 'react';
import { Link } from "wouter";
import { FaLinkedinIn, FaInstagram, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import { INTL_PHONE_DISPLAY, INTL_PHONE_URL, WHATSAPP_URL } from "@/lib/contact";
import { products as productList } from "@/lib/products";
import { services as serviceList } from "@/lib/services";
import { useFirstInteraction, useInView } from "@/hooks/use-first-interaction";

const FooterScene = React.lazy(() => import("../FooterScene"));

// Footer shows top 5 services to keep the column compact; "View all" links to the hub.
const TOP_SERVICES_COUNT = 5;
const services = serviceList.slice(0, TOP_SERVICES_COUNT).map((s) => ({
  name: s.name,
  href: `/services/${s.slug}`,
}));

const products = productList.map((p) => ({
  name: p.name,
  href: p.status === "coming-soon" ? `/products/${p.slug}` : p.liveUrl,
  logo: p.faviconSrc,
  comingSoon: p.status === "coming-soon",
}));

const company = [
  { name: "About", href: "/about" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
  { name: "Privacy", href: "/privacy" },
  { name: "Terms", href: "/terms" },
  { name: "US Citizenship Test – Privacy", href: "/us-citizenship-test/privacy" },
  { name: "US Citizenship Test – Terms", href: "/us-citizenship-test/terms" },
  { name: "Canadian Citizenship Test – Privacy", href: "/canadian-citizenship-test/privacy" },
  { name: "Canadian Citizenship Test – Terms", href: "/canadian-citizenship-test/terms" },
  { name: "UK Citizenship Test – Privacy", href: "/uk-citizenship-test/privacy" },
  { name: "UK Citizenship Test – Terms", href: "/uk-citizenship-test/terms" },
  { name: "Sudoku Cove – Privacy", href: "/sudoku-cove/privacy" },
  { name: "Sudoku Cove – Terms", href: "/sudoku-cove/terms" },
  { name: "Aqua Bliss – Privacy", href: "/aqua-bliss/privacy" },
  { name: "Aqua Bliss – Terms", href: "/aqua-bliss/terms" },
  { name: "WonderFind – Privacy", href: "/wonderfind/privacy" },
  { name: "WonderFind – Terms", href: "/wonderfind/terms" },
  { name: "XO Arena – Privacy", href: "/xo-arena/privacy" },
  { name: "XO Arena – Terms", href: "/xo-arena/terms" },
  { name: "Petalgrove – Privacy", href: "/petalgrove/privacy" },
  { name: "Petalgrove – Terms", href: "/petalgrove/terms" },
  { name: "Arrow Escape – Privacy", href: "/arrow-escape/privacy" },
  { name: "Arrow Escape – Terms", href: "/arrow-escape/terms" },
  { name: "Police Chief – Privacy", href: "/police-chief/privacy" },
  { name: "Police Chief – Terms", href: "/police-chief/terms" },
  { name: "REPZO – Privacy", href: "/repzo/privacy" },
  { name: "REPZO – Terms", href: "/repzo/terms" },
  { name: "Digital Khata: Udhar & Stock – Privacy", href: "/digital-khata/privacy" },
  { name: "Digital Khata: Udhar & Stock – Terms", href: "/digital-khata/terms" },
  { name: "Digital Khata: Udhar & Stock – Delete Account", href: "/digital-khata/delete-account" },
];

export function Footer() {
  const interacted = useFirstInteraction();
  const { ref: footerSceneRef, inView: nearViewport } = useInView<HTMLDivElement>('200px');
  const [showFooterScene, setShowFooterScene] = React.useState(false);

  // Mount once the footer is near the viewport after the visitor has interacted; keep it mounted after.
  React.useEffect(() => {
    if (interacted && nearViewport) setShowFooterScene(true);
  }, [interacted, nearViewport]);

  return (
    <footer className="w-full bg-[#0A0A0A] text-white border-t border-white/10 relative overflow-hidden">
      {/* Ambient FooterScene as background */}
      <div ref={footerSceneRef} className="absolute inset-0 z-0 opacity-20 pointer-events-none">
        {showFooterScene && (
          <Suspense fallback={null}>
            <FooterScene />
          </Suspense>
        )}
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* 4-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10 mb-16">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link href="/" className="inline-block mb-6">
              <img
                src="/branding/logo_bg_white.svg"
                alt="Orvexaco"
                className="h-12 md:h-14 w-auto"
                width={220}
                height={48}
              />
            </Link>
            <p className="font-mono text-xs text-[var(--muted)] uppercase tracking-widest leading-relaxed max-w-xs">
              A software studio building custom SaaS, AI products, and automation systems for businesses worldwide.
            </p>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <Link
              href="/services"
              className="font-mono text-[11px] uppercase tracking-widest text-white mb-6 block hover:opacity-60 transition-opacity"
            >
              Services
            </Link>
            <ul className="flex flex-col gap-3">
              {services.map((s) => (
                <li key={s.name}>
                  <Link
                    href={s.href}
                    className="font-mono text-[11px] uppercase tracking-wider text-[var(--muted)] hover:text-white transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <Link
                  href="/services"
                  className="font-mono text-[11px] uppercase tracking-wider text-white hover:opacity-60 transition-opacity inline-flex items-center gap-1"
                >
                  View all services <span aria-hidden="true">→</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div className="md:col-span-2">
            <Link
              href="/products"
              className="font-mono text-[11px] uppercase tracking-widest text-white mb-6 block hover:opacity-60 transition-opacity"
            >
              Products
            </Link>
            <ul className="flex flex-col gap-3">
              {products.map((p) => (
                <li key={p.name}>
                  <a
                    href={p.href}
                    target={p.comingSoon ? undefined : "_blank"}
                    rel={p.comingSoon ? undefined : "noopener noreferrer"}
                    className="font-mono text-[11px] uppercase tracking-wider text-[var(--muted)] hover:text-white transition-colors inline-flex items-center gap-2"
                  >
                    <img
                      src={p.logo}
                      alt=""
                      aria-hidden="true"
                      className="w-4 h-4 rounded bg-white/10 p-0.5"
                      width={16}
                      height={16}
                    />
                    {p.name}
                    {p.comingSoon ? <span>SOON</span> : <span aria-hidden="true">↗</span>}
                    {!p.comingSoon && <span className="sr-only">(opens in new tab)</span>}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="md:col-span-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white mb-6 block">
              Company
            </span>
            <ul className="flex flex-col gap-3">
              {company.map((c) => (
                <li key={c.name}>
                  <Link
                    href={c.href}
                    className="font-mono text-[11px] uppercase tracking-wider text-[var(--muted)] hover:text-white transition-colors"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            © 2026 Orvexaco. All rights reserved.
          </p>

          <div className="flex items-center gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Orvexaco on WhatsApp"
              className="w-9 h-9 grid place-items-center rounded-full border border-white/15 text-[#9A9A9A] hover:text-[#25D366] hover:border-[#25D366]/60 transition-colors"
            >
              <FaWhatsapp size={14} />
            </a>
            <a
              href={INTL_PHONE_URL}
              aria-label={`Call Orvexaco on ${INTL_PHONE_DISPLAY}`}
              className="w-9 h-9 grid place-items-center rounded-full border border-white/15 text-[#9A9A9A] hover:text-white hover:border-white/40 transition-colors"
            >
              <FaPhoneAlt size={13} />
            </a>
            <a
              href="https://www.linkedin.com/company/orvexaco/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Orvexaco on LinkedIn"
              className="w-9 h-9 grid place-items-center rounded-full border border-white/15 text-[#9A9A9A] hover:text-white hover:border-white/40 transition-colors"
            >
              <FaLinkedinIn size={14} />
            </a>
            <a
              href="https://www.instagram.com/orvexaco"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Orvexaco on Instagram"
              className="w-9 h-9 grid place-items-center rounded-full border border-white/15 text-[#9A9A9A] hover:text-white hover:border-white/40 transition-colors"
            >
              <FaInstagram size={14} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
